import { useTranslation, Translation } from 'react-i18next';
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Table, CellRenderProps, HelperTooltip, Segmented } from 'components';

import { shortenAddress, numFormat, CHAINS_DICT, getExplorerLink } from 'utils';
import { useCurrentAccountHistory } from '../../hooks';
import { AccountHistoryRecord } from '../../types';
import { useTheme } from '@/hooks';
import style from './index.module.css';
import cx from 'clsx';
import {
  IconRcAccountEmptyTransactions,
  IconRcAccountEmptyTransactionsDark,
} from '@/assets/account';
import { LinkParsedContent } from '@/components';

const PAGE_COUNT = 10;

export const AccountHistory = ({ className }: { className?: string }) => {
  const { t } = useTranslation();

  const { isDark } = useTheme();
  const AccountEmptyTransactionsIcon = isDark
    ? IconRcAccountEmptyTransactionsDark
    : IconRcAccountEmptyTransactions;
  const [start, setStart] = useState(0);
  const [type, setType] = useState<string>('');
  const { data, isLoading, refetch } = useCurrentAccountHistory(start, PAGE_COUNT, type);
  const total = useMemo(() => data?.pagination?.total || 0, [data?.pagination?.total]);

  useEffect(() => {
    const timer = setInterval(refetch, 5000);
    return () => clearInterval(timer);
  }, [refetch]);

  const pagination = useMemo(
    () => ({
      current: Math.floor(start / PAGE_COUNT) + 1,
      defaultPageSize: PAGE_COUNT,
      onChange: (page: number) => {
        setStart((page - 1) * PAGE_COUNT);
      },
      total,
      showQuickJumper: true,
    }),
    [start, total],
  );

  const typesList = useMemo(
    () => [
      {
        label: t('AccountHistory.all'),
        value: '',
      },
      {
        label: t('AccountHistory.deposit'),
        value: '0',
      },
      {
        label: t('AccountHistory.withdraw'),
        value: '2',
      },
    ],
    [t],
  );

  const hasNoRecords = useMemo(
    () => !isLoading && data?.actions?.length === 0,
    [data?.actions.length, isLoading],
  );

  const handleTypeChange = useCallback((type: string) => {
    setStart(0);
    setType(type);
  }, []);

  return (
    <div className={cx(style.container, className)}>
      <div className={style.header}>
        <span className={style.title}>{t('AccountHistory.16uypo4')}</span>
        <Segmented
          color="gray"
          hasSplit={false}
          options={typesList}
          value={type}
          onChange={handleTypeChange}
          className={style.tabs}
        />
      </div>

      <div>
        <Table
          className={style.mainTable}
          columns={columns}
          data={data?.actions}
          loading={isLoading}
          pagination={hasNoRecords ? undefined : pagination}
          customChildren={
            hasNoRecords ? (
              <div className={style.emptyTable}>
                <AccountEmptyTransactionsIcon className={style.emptyImage} />
                <span className={style.emptyText}>{t('AccountHistory.180vaz8')}</span>
              </div>
            ) : null
          }
        />
      </div>
    </div>
  );
};

const columns = [
  {
    key: 'create_at',
    title: <Translation>{t => t('AccountHistory.1jvfx')}</Translation>,
    width: '140px',
    render: ({ cellData }: CellRenderProps<AccountHistoryRecord>) => (
      <div className={style.commonCell}>{cellData}</div>
    ),
  },
  {
    key: 'message',
    title: <Translation>{t => t('AccountHistory.wchdg6')}</Translation>,
    width: '280px',
    render: ({ cellData }: CellRenderProps<AccountHistoryRecord>) => (
      <HelperTooltip title={<LinkParsedContent content={cellData} />} placement="topLeft">
        <div className={cx(style.commonCell, style.messageCell)}>{cellData}</div>
      </HelperTooltip>
    ),
  },
  {
    key: 'statusWithConfirmNumbers',
    title: <Translation>{t => t('AccountHistory.154b66q')}</Translation>,
    width: '100px',
    render: ({ cellData }: CellRenderProps<any>) => {
      const { value, confirmNumbers, pendingTooltip } = cellData;
      return (
        <HelperTooltip title={pendingTooltip} placement="topLeft">
          <div
            className={cx(
              style.commonCell,
              style.statusCell,
              value === 'Failed' ? style.failed : null,
            )}>
            {value}
            {confirmNumbers ? <span className={style.confirmNumbers}>{confirmNumbers}</span> : null}
          </div>
        </HelperTooltip>
      );
    },
  },
  {
    key: 'balanceChange',
    title: <Translation>{t => t('AccountHistory.1oglaqc')}</Translation>,
    width: '120px',
    render: ({ cellData }: CellRenderProps<any>) => {
      const { value, status } = cellData;
      const isAdd = value > 0;
      return (
        <div className={cx(style.commonCell, style.amountChangeCell, isAdd ? style.isAdd : null)}>
          {status === 'Pending' ? (
            <div className={style.loading}>
              <span></span>
              <span></span>
              <span></span>
            </div>
          ) : status === 'Completed' ? (
            `${isAdd ? '+' : '-'} ${numFormat(Math.abs(value), 2, '$')}`
          ) : (
            '--'
          )}
        </div>
      );
    },
  },
  {
    key: 'chain_id',
    title: <Translation>{t => t('AccountHistory.e71p3a')}</Translation>,
    width: '80px',
    render: ({ cellData }: CellRenderProps<AccountHistoryRecord>) =>
      cellData ? (
        <div className={cx(style.commonCell, style.chainCell)}>
          {CHAINS_DICT[cellData]?.name || ''}
        </div>
      ) : (
        <div className={style.commonCell}>--</div>
      ),
  },
  {
    key: 'tx_id',
    title: <Translation>{t => t('AccountHistory.bv2vtv')}</Translation>,
    width: '120px',
    render: ({ cellData, row }: CellRenderProps<AccountHistoryRecord>) =>
      cellData && row.chain_id ? (
        <a
          href={getExplorerLink(row.chain_id, cellData)}
          className={cx(style.commonCell, style.txCell)}
          rel="noreferrer"
          target="_blank">{`${shortenAddress(cellData, 6, 4)}`}</a>
      ) : (
        <div className={style.commonCell}>--</div>
      ),
  },
];
